import { Link } from "react-router-dom";
import { Bell, ExternalLink, KeyRound, Plug, Settings, UserRound } from "lucide-react";
import { TrustedDevicesSection } from "../components/TrustedDevicesSection";
import { PushNotificationSettings } from "../components/PushNotificationSettings";
import { Eyebrow } from "../components/brand";
import { useAllowedApps } from "../hooks/useAllowedApps";
import { findShellApp } from "../lib/apps";
import { INSTANCE } from "../lib/instance";
import { getAppLabel, isAppEnabledForInstance } from "../../../shared/app-catalog";

function SectionCard({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="card p-5">
      <div className="flex items-center gap-3 mb-4">
        <span
          className="flex items-center justify-center w-8 h-8 rounded"
          style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
        >
          {icon}
        </span>
        <h2
          className="text-sm font-semibold uppercase tracking-wider flex-1"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {title}
        </h2>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function AppSettingsLink({ label, href }: { label: string; href: string }) {
  return (
    <a
      href={href}
      className="flex items-center gap-3 px-3 py-2.5 rounded transition-colors"
      style={{ background: "var(--color-bg)", border: "1px solid var(--color-border)" }}
      onMouseEnter={(event) => { (event.currentTarget as HTMLElement).style.borderColor = "var(--accent)"; }}
      onMouseLeave={(event) => { (event.currentTarget as HTMLElement).style.borderColor = "var(--color-border)"; }}
    >
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold" style={{ color: "var(--color-foreground)" }}>
          {label}
        </div>
        <div className="text-[11px] mt-0.5 truncate" style={{ color: "var(--color-muted)" }}>
          Einstellungen in dieser Anwendung öffnen
        </div>
      </div>
      <ExternalLink size={12} style={{ color: "var(--color-muted)", flexShrink: 0 }} />
    </a>
  );
}

/**
 * Persönliche Einstellungen sind für jeden angemeldeten Benutzer erreichbar.
 * Verweise auf Satelliten erscheinen erst, wenn die App im vorhandenen
 * Instanzprofil aktiv und für das Konto freigegeben ist.
 */
export function EinstellungenPage() {
  const { allowed, loading } = useAllowedApps();
  const appSettings = ["notes", "briefing"].flatMap((id) => {
    const app = findShellApp(id);
    if (!app?.appKey || !isAppEnabledForInstance(app.appKey, INSTANCE) || !allowed?.has(app.appKey)) return [];
    return [{ key: app.appKey, label: getAppLabel(app.appKey, app.title), href: `/#${app.route}` }];
  });

  return (
    <div className="animate-in max-w-3xl">
      <div className="mb-8">
        <Eyebrow>Konto · Einstellungen</Eyebrow>
        <h1 className="text-[22px] font-bold tracking-tight mt-2" style={{ fontFamily: "var(--font-display)" }}>
          Persönliche Einstellungen
        </h1>
        <p className="text-xs mt-1" style={{ color: "var(--color-muted)" }}>
          Einstellungen für Ihr eigenes Konto und Ihre freigegebenen Anwendungen.
        </p>
      </div>

      <div className="flex flex-col gap-5">
        <SectionCard icon={<UserRound size={16} />} title="Konto und Sicherheit">
          <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
            Passwort, Zwei-Faktor-Authentifizierung und Kontoinformationen werden getrennt von der Systemadministration verwaltet.
          </p>
          <Link to="/hub/konto" className="btn btn-secondary text-xs w-fit">
            <KeyRound size={13} /> Konto und Sicherheit öffnen
          </Link>
        </SectionCard>

        <TrustedDevicesSection />

        <SectionCard icon={<Bell size={16} />} title="Benachrichtigungen">
          <PushNotificationSettings />
        </SectionCard>

        {!loading && appSettings.length > 0 ? (
          <SectionCard icon={<Plug size={16} />} title="Anwendungseinstellungen">
            <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
              Diese Verweise sind nur für Anwendungen sichtbar, die in dieser Instanz aktiv und für Ihr Konto freigegeben sind.
            </p>
            {appSettings.map((app) => <AppSettingsLink key={app.key} label={app.label} href={app.href} />)}
          </SectionCard>
        ) : null}

        <div className="text-center py-4" style={{ color: "var(--color-muted)" }}>
          <p className="text-[10px] font-mono uppercase tracking-widest">{INSTANCE.productName} · Persönliche Einstellungen</p>
        </div>
      </div>
    </div>
  );
}
